import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSEO } from "@/lib/seo";

const H2 = ({ children }) => <h2 className="font-display font-bold text-2xl mt-10 mb-3 text-white">{children}</h2>;
const P = ({ children }) => <p className="text-zinc-400 leading-relaxed mb-4">{children}</p>;

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  useSEO({
    title: "Privacy Policy | BrandToBuilt",
    description: "How BrandToBuilt (Ainsleys Touch Ltd) collects, uses and protects your data.",
    path: "/privacy",
  });

  return (
    <div className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-zinc-400 hover:text-[#ccff00] transition-colors mb-8" data-testid="privacy-back">
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-4">Privacy Policy</h1>
        <p className="text-zinc-500 text-sm mb-10">Last updated: 31 August 2026</p>

        <H2>Who we are</H2>
        <P>BrandToBuilt is a trading name of Ainsleys Touch Ltd, a company registered in England and Wales (company number 17294211), registered office at 66 Paul Street, London, England, EC2A 4NA ("we", "us", "our").</P>

        <H2>What we collect</H2>
        <P>When you use our Website Score tool, Business Intake form, or buy a package or plan, we collect the details you give us — your name, business name, phone number, email address, and any information you type or speak about your business. If you contact us via WhatsApp or email, we keep that conversation.</P>
        <P>Payment information is handled entirely by Stripe, our payment processor. We never see or store your full card details.</P>
        <P>If you accept analytics cookies (see our <a href="/cookies" className="text-[#ccff00] underline">Cookie Policy</a>), we also collect anonymised usage data via PostHog to understand how visitors use the site.</P>

        <H2>Why we collect it</H2>
        <P>We use your information to deliver the service you've paid for (build and launch your website/brand), to keep you updated on your order (deadline confirmations, receipts, referral codes, care plan reminders, and — if you cancel a plan — an occasional win-back offer), to respond to enquiries, and to improve our site and service.</P>

        <H2>Automated emails we may send you</H2>
        <P>Depending on what you buy, you may automatically receive: an order receipt, a launch deadline confirmation, a referral code once your project is delivered (plus a thank-you reward if a friend uses it), monthly reminders of what's included in your care plan, an occasional plan-upgrade suggestion, a reminder if you start checkout but don't finish, and a win-back offer if you cancel a subscription. You can opt out of any of these at any time by emailing us.</P>

        <H2>Who we share it with</H2>
        <P>Stripe (payments), our email delivery infrastructure (sending you order and account emails), our AI providers (to power the Website Score and Business Intake tools — your submitted text is processed to generate suggestions for you, not used to train public AI models), and PostHog (analytics, only with your consent). We do not sell your data.</P>

        <H2>How long we keep it</H2>
        <P>We keep your information for as long as needed to deliver your service, and afterwards as required by UK tax and accounting law (typically up to 6 years for financial records).</P>

        <H2>Your rights</H2>
        <P>Under UK GDPR you have the right to access, correct, or ask us to delete your personal data, and to object to how we use it. To exercise any of these rights, email us at <a href="mailto:hello@brandtobuilt.co.uk" className="text-[#ccff00] underline">hello@brandtobuilt.co.uk</a>. You can also complain to the Information Commissioner's Office (ico.org.uk) if you're unhappy with how we've handled your data.</P>

        <H2>Contact</H2>
        <P>Ainsleys Touch Ltd, 66 Paul Street, London, England, EC2A 4NA — <a href="mailto:hello@brandtobuilt.co.uk" className="text-[#ccff00] underline">hello@brandtobuilt.co.uk</a></P>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
